import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { InfoCircle, TickCircle } from "iconsax-reactjs";
import { cn } from "lib/utils";

const formatBalance = (value) =>
    Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

export default function SelectBankProviderModal({
    isOpen,
    onClose,
    onSelect,
    selectedId,
    providers,
    currency,
}) {
    const [selected, setSelected] = useState(selectedId ?? null);

    useEffect(() => {
        if (!isOpen) return;
        setSelected(selectedId ?? null);
    }, [isOpen, selectedId]);

    if (!isOpen) return null;

    const list = Array.isArray(providers) ? providers : [];
    const ccy = String(currency || "").toUpperCase();

    return (
        <div className="fixed inset-0 z-[999] bg-black/30 flex items-center justify-center px-4">
            <div className="bg-white rounded-2xl w-full max-w-xl p-6 relative">
                <button className="absolute right-4 top-4" onClick={onClose}>
                    <X size={20} />
                </button>

                <h2 className="text-xl font-semibold mb-4">Select bank account</h2>

                <div className="bg-[#F1F3FF] text-sm text-[#4B4E68] flex gap-2 items-center rounded-lg px-4 py-2 mb-3">
                    <InfoCircle size="22" color="#2C53F2" variant="Bold" />
                    Choose which of your bank accounts to send from.
                </div>

                <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
                    {list.map((provider, idx) => {
                        const id = provider?._id || provider?.accountNumber || idx;

                        return (
                            <button
                                key={id}
                                onClick={() => {
                                    setSelected(id);
                                    onSelect?.(provider);
                                    onClose();
                                }}
                                className={cn(
                                    "w-full flex items-center justify-between px-3 py-3 border rounded-xl text-left transition",
                                    selected === id
                                        ? "border-[#6F00FF] border-solid border-1 shadow-sm"
                                        : "border-gray-200"
                                )}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="h-10 w-10 shrink-0 rounded-full bg-[#F2F3FC] grid place-items-center text-sm text-[#667085] uppercase">
                                        {String(provider?.bankName || provider?.bank || "B").slice(
                                            0,
                                            2
                                        )}
                                    </span>

                                    <div className="min-w-0">
                                        <p className="font-medium truncate">
                                            {provider?.bankName || provider?.bank || "Bank"}
                                        </p>
                                        <p className="text-sm text-gray-500 truncate">
                                            {provider?.accountNumber || "—"}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 shrink-0">
                                    {Number.isFinite(Number(provider?.balance)) && (
                                        <span className="text-sm text-[#6B7280]">
                                            {formatBalance(provider.balance)} {ccy}
                                        </span>
                                    )}
                                    <TickCircle
                                        className={
                                            selected === id ? "text-[#6F00FF]" : "text-transparent"
                                        }
                                        variant={selected === id ? "Bold" : "Outline"}
                                        size={20}
                                    />
                                </div>
                            </button>
                        );
                    })}

                    {list.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-6">
                            No bank accounts found.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
